import { h } from 'preact';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { useEffect, useState } from 'preact/hooks';
import { useStore } from 'effector-react';

import { navigate } from 'libs/router';
import { Container } from 'ui/container';
import { Button } from 'ui/atoms/button';
import { countryCode$ } from 'model/main.model';
import { routes } from 'features/fallback/routes';
import { sessionConfig$ } from 'model/session.model';
import { sendEvent } from 'model/helpers/events.model';
import { FallbackLayout } from 'features/fallback/ui/fallback-layout';
import { screenHistoryPush, ScreenName } from 'model/helpers/screens-history';
import { localizationOptions$, useLocalization } from 'model/localization.model';

export const WelcomeView = () => {
	const t = useLocalization();
	const sessionConfig = useStore(sessionConfig$);
	const code = useStore(countryCode$);
	const options = useStore(localizationOptions$);
	const [start] = useState(Date.now());

	const { company, urls } = sessionConfig || {};

	const handleGetStarted = () => {
		navigate(routes.link);
		sendEvent({
			event: 'welcome_screen_continue',
			event_attributes: {
				type: 'desktop',
				duration: Date.now() - start,
			},
		});
	};

	useEffect(() => {
		screenHistoryPush(ScreenName.welcome_desktop);
		setTimeout(() => {
			sendEvent({
				event: 'welcome_screen_shown',
				event_attributes: {
					type: 'desktop',
					ua: window.navigator.userAgent,
					geoip_country: code,
					language: options.language || 'en',
					from: null,
				},
			});
		}, 20);
	}, []);

	const items = [{
		icon: <PassportIcon/>,
		title: t('fallback.doc.title'),
		subtitle: t('fallback.doc.subtitle'),
	}, {
		icon: <SmartphoneIcon/>,
		title: t('fallback.phone.title'),
		subtitle: t('fallback.phone.subtitle'),
	}];

	return (
		<FallbackLayout>
			<Container>
				<Title className={titleClass}>
					{t('welcome.title')}
				</Title>
				<Subtitle className={subtitleClass}>
					{t('welcome.subtitle', { company })}
				</Subtitle>
				{items.map((item, index) => (
					<ItemContainer key={index}>
						{item.icon}
						<ItemContent>
							<ItemTitle>{item.title}</ItemTitle>
							<ItemSubtitle>{item.subtitle}</ItemSubtitle>
						</ItemContent>
					</ItemContainer>
				))}
				<Text
					dangerouslySetInnerHTML={{
						__html: t('fallback.text', {
							company: company,
							'welcome.hint.tos.sub': `<a target="_blank" rel="nofollow noopener noreferrer" href="${t(urls?.tos_url)}">${t('welcome.hint.tos.sub')}</a>`,
						}),
					}}
				/>
				<Button
					name={'welcome.btn'}
					onClick={handleGetStarted}
					type={'primary'}
					className={btnClass}
				>
					{t('welcome.btn')}
				</Button>
			</Container>
		</FallbackLayout>
	);
};

export const Title = styled.h2`
  margin: 0;
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: var(--text-primary);

  @media (max-width: 560px) {
    font-size: 20px;
    line-height: 28px;
  }
`;
export const Subtitle = styled.h3`
  margin: 0 0 8px;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 28px;
  color: var(--text-secondary);

  @media (max-width: 560px) {
    font-weight: normal;
    font-size: 18px;
    line-height: 24px;
  }
`;
const titleClass = css`
  margin-top: 16px;
  margin-bottom: 4px;
`;
const subtitleClass = css`
  font-weight: 400;
  margin-bottom: 16px;
`;
const btnClass = css`
  width: 100%;
  text-align: center;
  margin: 0 auto 16px;
`;
const ItemContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;
const ItemContent = styled.div`
  margin-left: 12px;
`;
const ItemTitle = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: var(--text-primary);
`;
const ItemSubtitle = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 18px;
  color: var(--text-primary);
`;
const Text = styled.div`
  margin: auto 0 16px;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  color: var(--text-secondary);
`;

const PassportIcon = () => (
	<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M24.75 2.75H4.75V29.25H24.75C26.125 29.25 27.25 28.125 27.25 26.75V5.25C27.25 3.875 26.125 2.75 24.75 2.75Z" fill="white"/>
		<path
			d="M24.75 30H4.75C4.325 30 4 29.675 4 29.25V2.75C4 2.325 4.325 2 4.75 2H24.75C26.55 2 28 3.45 28 5.25V26.75C28 28.55 26.55 30 24.75 30ZM5.5 28.5H24.75C25.725 28.5 26.5 27.725 26.5 26.75V5.25C26.5 4.275 25.725 3.5 24.75 3.5H5.5V28.5Z"
			fill="var(--text-primary)"/>
		<path
			d="M18.5 23H13.5C13.075 23 12.75 22.675 12.75 22.25C12.75 21.825 13.075 21.5 13.5 21.5H18.5C18.925 21.5 19.25 21.825 19.25 22.25C19.25 22.675 18.925 23 18.5 23Z"
			fill="var(--primary-color)"/>
		<path
			d="M12.85 9.62524C11.925 10.3752 11.25 11.4752 11.075 12.7252C11.05 12.8752 11.175 13.0002 11.325 13.0002H13C13.15 13.0002 13.25 12.9002 13.25 12.7502V9.82524C13.25 9.62524 13 9.50024 12.85 9.62524Z"
			fill="var(--primary-color)"/>
		<path
			d="M15 13H17C17.15 13 17.25 12.9 17.25 12.75C17.25 12.75 17.25 9.75 17.25 8.875C17.25 8.75 17.175 8.65 17.075 8.625C16.725 8.55 16.375 8.5 16 8.5C15.625 8.5 15.275 8.55 14.925 8.625C14.8 8.65 14.75 8.75 14.75 8.875C14.75 9 14.75 12.75 14.75 12.75C14.75 12.9 14.85 13 15 13Z"
			fill="var(--primary-color)"/>
		<path
			d="M11.1751 14.825C11.4501 15.85 12.0501 16.725 12.8501 17.375C13.0001 17.5 13.2501 17.375 13.2501 17.175V14.75C13.2501 14.6 13.1501 14.5 13.0001 14.5H11.4251C11.2501 14.5 11.1251 14.65 11.1751 14.825Z"
			fill="var(--primary-color)"/>
		<path
			d="M20.95 12.7252C20.75 11.4752 20.1 10.4002 19.175 9.62524C19 9.50024 18.75 9.62524 18.75 9.82524V12.7502C18.75 12.9002 18.85 13.0002 19 13.0002H20.7C20.85 13.0002 20.95 12.8752 20.95 12.7252Z"
			fill="var(--primary-color)"/>
		<path
			d="M19.15 17.375C19.95 16.725 20.55 15.85 20.825 14.825C20.875 14.675 20.75 14.5 20.575 14.5H19C18.85 14.5 18.75 14.6 18.75 14.75V17.175C18.75 17.375 19 17.5 19.15 17.375Z"
			fill="var(--primary-color)"/>
		<path
			d="M14.75 14.75V18.15C14.75 18.275 14.825 18.375 14.95 18.4C15.3 18.475 15.65 18.525 16 18.525C16.375 18.525 16.725 18.475 17.05 18.4C17.175 18.375 17.25 18.275 17.25 18.15V14.75C17.25 14.6 17.15 14.5 17 14.5H15C14.85 14.5 14.75 14.6 14.75 14.75Z"
			fill="var(--primary-color)"/>
	</svg>
);
const SmartphoneIcon = () => (
	<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M18.5 30.9998V28.2498C18.5 27.2748 18.875 26.3498 19.525 25.6498L26.3 18.4748C27.25 17.4748 27.2 15.8998 26.2 14.9498C25.2 13.9998 23.625 14.0498 22.675 15.0498L19.3 18.5998C19.625 18.1498 19.95 17.7248 20.3 17.2998L26.575 10.0498C27.575 8.8998 27.45 7.1748 26.3 6.1748C25.15 5.1748 23.425 5.2998 22.425 6.4498L16.15 13.6998C16 13.8748 15.85 14.0498 15.7 14.2498C16.125 13.4748 16.6 12.6998 17.125 11.9748L21.75 5.3248C22.625 4.0748 22.3 2.3748 21.075 1.4998C19.825 0.624797 18.125 0.949797 17.25 2.1748L12.6 8.8248C12.35 9.1998 12.1 9.5748 11.85 9.9498L14.425 4.9998C15.125 3.6498 14.6 1.9998 13.25 1.2998C11.9 0.599797 10.25 1.1248 9.55004 2.4748L6.97504 7.4498C5.22504 10.7998 4.07504 14.4248 3.57504 18.1498V31.7498"
			fill="white"/>
		<path
			d="M3.62509 30.9999C2.25009 25.1499 4.52509 20.2499 8.30009 16.0249L9.35009 14.8499C10.1751 13.9249 11.6001 13.8249 12.5251 14.6499C13.4501 15.4749 13.5501 16.8999 12.7251 17.8249L11.6501 18.9999C8.82509 22.1749 9.05009 25.2499 10.9251 28.4999H18.5001V31.7499H3.75009L3.62509 30.9999Z"
			fill="white"/>
		<path
			d="M3.62509 30.9999C2.25009 25.1499 4.52509 20.2499 8.30009 16.0249L9.35009 14.8499C10.1751 13.9249 11.6001 13.8249 12.5251 14.6499C13.4501 15.4749 13.5501 16.8999 12.7251 17.8249L11.6501 18.9999C8.82509 22.1749 9.05009 25.2499 10.9251 28.4999H18.5001V31.7499H3.75009L3.62509 30.9999Z"
			fill="white"/>
		<path
			d="M18.5001 31.7498C18.0751 31.7498 17.7501 31.4248 17.7501 30.9998V28.2498C17.7501 27.0748 18.2001 25.9748 19.0001 25.1248L25.7751 17.9498C26.4501 17.2498 26.4001 16.1498 25.7001 15.4748C25.3501 15.1498 24.9251 14.9748 24.4501 14.9998C23.9751 15.0248 23.5501 15.1998 23.2251 15.5498L19.8501 19.1248C19.5751 19.3998 19.1251 19.4498 18.8251 19.1998C18.5251 18.9498 18.4501 18.4998 18.6751 18.1998C19.0251 17.7248 19.3751 17.2498 19.7251 16.8498L26.0001 9.59985C26.7251 8.77485 26.6251 7.49985 25.8001 6.77485C24.9751 6.04985 23.7001 6.14985 22.9751 6.97485L16.7001 14.2248C16.5751 14.3748 16.4251 14.5498 16.3001 14.6998L16.2501 14.7498C16.0001 15.0498 15.5751 15.1248 15.2501 14.8998C14.9251 14.6748 14.8251 14.2498 15.0251 13.9248C15.4751 13.0998 15.9751 12.3248 16.4751 11.5748L21.1001 4.92485C21.4001 4.47485 21.5251 3.94985 21.4251 3.42485C21.3251 2.89985 21.0501 2.44985 20.6001 2.12485C20.1501 1.82485 19.6251 1.69985 19.1001 1.79985C18.5751 1.89985 18.1251 2.17485 17.8001 2.62485L13.2251 9.27485C13.0001 9.59985 12.7501 9.94985 12.5001 10.3748C12.2751 10.7248 11.8501 10.8248 11.5001 10.6248C11.1501 10.4248 11.0251 9.97485 11.2001 9.62485L13.7751 4.67485C14.0251 4.19985 14.0751 3.64985 13.9001 3.14985C13.7501 2.64985 13.4001 2.22485 12.9251 1.97485C12.4501 1.72485 11.9001 1.67485 11.4001 1.84985C10.9001 1.99985 10.4751 2.34985 10.2251 2.82485L7.62513 7.79985C5.92513 11.0748 4.80013 14.5998 4.30013 18.2748C4.25013 18.6748 3.87513 18.9748 3.45013 18.9248C3.05013 18.8748 2.75013 18.4998 2.80013 18.0748C3.32513 14.2248 4.50013 10.5498 6.30013 7.12485L8.90013 2.12485C9.32513 1.29985 10.0501 0.674847 10.9501 0.399847C11.8501 0.124847 12.8001 0.199847 13.6251 0.624847C14.4501 1.04985 15.0751 1.77485 15.3501 2.67485C15.4251 2.89985 15.4751 3.12485 15.5001 3.37485L16.6251 1.74985C17.1501 0.974847 17.9501 0.474847 18.8751 0.299847C19.8001 0.124847 20.7251 0.349847 21.5001 0.874847C22.2751 1.39985 22.7751 2.19985 22.9501 3.12485C23.0751 3.87485 22.9751 4.59985 22.6501 5.27485C23.9251 4.47485 25.6001 4.57485 26.7751 5.59985C28.2251 6.84985 28.4001 9.07485 27.1501 10.5248L24.6001 13.4998C25.4001 13.5248 26.1501 13.8248 26.7501 14.3748C28.0501 15.5998 28.1001 17.6748 26.8751 18.9748L20.1001 26.1498C19.5751 26.7248 19.2751 27.4498 19.2751 28.2248V30.9748C19.2501 31.4248 18.9251 31.7498 18.5001 31.7498Z"
			fill="var(--text-primary)"/>
		<path
			d="M10.325 28.5H21.95C23.225 28.5 24.25 27.475 24.25 26.2V3.275C24.25 2.025 23.225 1 21.95 1H10.05C8.775 1 7.75 2.025 7.75 3.3L7.825 26.025C7.825 27.375 8.95 28.5 10.325 28.5Z"
			fill="white"/>
		<path opacity="0.3"
		      d="M9.35004 14.85C10.175 13.925 11.6 13.825 12.525 14.65C13.45 15.475 13.55 16.9 12.725 17.825L11.65 19C10.875 19.9 10.325 20.775 9.97504 21.65C10.3 21.725 10.65 21.75 11 21.75C14.05 21.75 16.5 19.3 16.5 16.25C16.5 13.2 14.05 10.75 11 10.75C9.75004 10.75 8.62504 11.15 7.70004 11.85L7.72504 16.7C7.92504 16.475 8.10004 16.25 8.30004 16.025L9.35004 14.85Z"
		      fill="var(--primary-color)"/>
		<path
			d="M16 24C15.6685 24 15.3505 24.1317 15.1161 24.3661C14.8817 24.6005 14.75 24.9185 14.75 25.25C14.75 25.5815 14.8817 25.8995 15.1161 26.1339C15.3505 26.3683 15.6685 26.5 16 26.5C16.3315 26.5 16.6495 26.3683 16.8839 26.1339C17.1183 25.8995 17.25 25.5815 17.25 25.25C17.25 24.9185 17.1183 24.6005 16.8839 24.3661C16.6495 24.1317 16.3315 24 16 24Z"
			fill="var(--primary-color)"/>
		<path
			d="M3.62496 31.75C3.27496 31.75 2.97496 31.525 2.89996 31.175C1.64996 25.75 3.22496 20.625 7.74996 15.525L8.79996 14.35C9.32496 13.75 10.075 13.4 10.875 13.35C11.675 13.3 12.45 13.575 13.05 14.1C13.65 14.625 14 15.375 14.05 16.175C14.1 16.975 13.825 17.75 13.3 18.35L12.225 19.5C9.87496 22.15 9.67496 24.825 11.575 28.125C11.775 28.475 11.65 28.95 11.3 29.15C10.95 29.35 10.475 29.225 10.275 28.875C8.02496 24.975 8.27496 21.675 11.1 18.5L12.15 17.325C12.425 17.025 12.55 16.65 12.525 16.25C12.5 15.85 12.325 15.475 12.025 15.225C11.725 14.95 11.35 14.825 10.95 14.85C10.55 14.875 10.175 15.05 9.92496 15.35L8.87496 16.525C4.69996 21.225 3.22496 25.925 4.37496 30.825C4.47496 31.225 4.22496 31.625 3.82496 31.725C3.72496 31.75 3.67496 31.75 3.62496 31.75Z"
			fill="var(--primary-color)"/>
		<path
			d="M21.875 29.25H13.825C13.4 29.25 13.075 28.925 13.075 28.5C13.075 28.075 13.4 27.75 13.825 27.75H21.875C22.725 27.75 23.425 27.05 23.425 26.2V3.3C23.425 2.45 22.725 1.75 21.875 1.75H9.97496C9.12496 1.75 8.42496 2.45 8.42496 3.3V11.975C8.42496 12.4 8.09996 12.725 7.67496 12.725C7.24996 12.725 6.92496 12.4 6.92496 11.975V3.3C6.92496 1.625 8.27496 0.25 9.97496 0.25H21.9C23.575 0.25 24.95 1.625 24.95 3.3V26.225C24.925 27.875 23.55 29.25 21.875 29.25Z"
			fill="var(--primary-color)"/>
	</svg>
);
